import { useEffect } from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft,
  MessageCircle,
  Cable,
  Server,
  Network,
  ShieldCheck,
  Wrench,
  FileCheck2,
  Layers,
  ClipboardCheck,
  CheckCircle2,
  Cpu,
} from "lucide-react";
import Header from "../components/nexus/Header";
import Footer from "../components/nexus/Footer";
import { SITE } from "../lib/site";

const SERVICES = [
  {
    icon: FileCheck2,
    title: "Fusão e certificação de fibra óptica",
    desc: "Emenda óptica profissional com máquina de fusão e certificação via OTDR. Entregamos o laudo técnico completo com documentação do enlace.",
  },
  {
    icon: Server,
    title: "Instalações de data centers",
    desc: "Projeto, montagem e ativação de ambientes críticos — desde a climatização e piso técnico até o cabeamento estruturado e energização redundante.",
  },
  {
    icon: Cable,
    title: "Lançamento de cabos de fibra e UTP",
    desc: "Passagem organizada de cabos ópticos (backbone) e UTP (Cat 6/6A), com identificação, fixação profissional e respeito à infraestrutura existente.",
  },
  {
    icon: Layers,
    title: "Montagem e organização de racks",
    desc: "Montagem de racks de parede e piso, organização dos patches com gestão de cabos, identificação e documentação as-built.",
  },
  {
    icon: Network,
    title: "Estruturação de redes corporativas",
    desc: "LAN empresarial, Wi-Fi de alta densidade, VLANs, segmentação e boas práticas de segurança para garantir performance e controle.",
  },
  {
    icon: ShieldCheck,
    title: "Projetos de infraestrutura crítica",
    desc: "Consultoria técnica para ambientes onde disponibilidade é essencial. Redundância, contingência e planejamento de capacidade.",
  },
  {
    icon: Wrench,
    title: "Empreita técnica especializada",
    desc: "Executamos obras de infraestrutura de redes em regime de empreita — prazo, escopo e entrega definidos com transparência.",
  },
  {
    icon: ClipboardCheck,
    title: "Documentação e certificação",
    desc: "Todo projeto entrega documentação técnica completa: diagramas, identificação de pontos, relatório de certificação e memoriais.",
  },
];

const PROCESS = [
  {
    step: "01",
    title: "Diagnóstico & levantamento",
    desc: "Visita técnica, entendimento do cenário, mapeamento da infraestrutura e levantamento de requisitos.",
  },
  {
    step: "02",
    title: "Projeto técnico",
    desc: "Elaboração do projeto dimensionado, lista de materiais, cronograma e orçamento transparente.",
  },
  {
    step: "03",
    title: "Execução especializada",
    desc: "Equipe própria e capacitada, materiais homologados, operação limpa e com respeito ao ambiente do cliente.",
  },
  {
    step: "04",
    title: "Certificação & entrega",
    desc: "Testes, certificação OTDR/cabeamento, entrega da documentação as-built e treinamento de operação.",
  },
];

const WPP_INFRA =
  "https://wa.me/5516981845736?text=" +
  encodeURIComponent(
    "Olá, Grupo Nexus! Gostaria de falar sobre um projeto de Infraestrutura Técnica."
  );

export const InfraestruturaPage = () => {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
    document.title = "Infraestrutura Técnica — Grupo Nexus";
  }, []);

  return (
    <>
      <Header />
      <main className="bg-[#050505] text-white">
        {/* HERO */}
        <section className="relative pt-32 md:pt-40 pb-24 overflow-hidden">
          <div
            className="absolute inset-0 bg-cover bg-center opacity-60"
            style={{
              backgroundImage:
                "url(https://static.prod-images.emergentagent.com/jobs/45876ead-02aa-42fd-bdbd-b2c7f606006c/images/ffee4e28c0ab598b7a4eb09798d58c27474667942f1f5b5dff0cc462a17910e3.png)",
            }}
            aria-hidden
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-[#050505]" aria-hidden />
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(76,46,155,0.35),transparent_60%)]" aria-hidden />

          <div className="relative max-w-7xl mx-auto px-6 md:px-8">
            <Link
              to="/"
              data-testid="back-home"
              className="inline-flex items-center gap-2 text-[13px] text-white/70 hover:text-white mb-10"
            >
              <ArrowLeft size={14} /> Voltar para o Grupo Nexus
            </Link>

            <div className="max-w-4xl">
              <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-1.5 backdrop-blur-md">
                <Cpu size={12} className="text-cyan-300" />
                <span className="overline text-white/80">Unidade 01 · Infraestrutura Técnica</span>
              </div>

              <h1 className="font-display mt-7 text-[40px] leading-[1.02] sm:text-5xl md:text-6xl lg:text-[72px] font-light tracking-tight">
                Do <span className="nx-rainbow-text font-medium">backbone</span> ao{" "}
                <span className="text-white/85 font-medium">rack</span> — engenharia que{" "}
                <span className="italic font-light">sustenta</span> a sua operação.
              </h1>

              <p className="mt-7 max-w-2xl text-base md:text-lg text-[#b3b0c2] leading-relaxed">
                Somos a frente de execução técnica do Grupo Nexus. Projetamos, implantamos e
                certificamos infraestrutura de redes e data centers — com equipe própria, materiais
                homologados e padrão de acabamento profissional.
              </p>

              <div className="mt-9 flex flex-col sm:flex-row gap-3">
                <a
                  href={WPP_INFRA}
                  target="_blank"
                  rel="noreferrer noopener"
                  data-testid="infra-hero-whatsapp"
                  className="nx-btn-primary"
                >
                  <MessageCircle size={16} /> Solicitar orçamento
                </a>
                <a href="#servicos" className="nx-btn-secondary">
                  Ver serviços
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* SERVICES GRID */}
        <section id="servicos" className="relative py-20 md:py-28 border-t border-white/5">
          <div className="max-w-7xl mx-auto px-6 md:px-8">
            <div className="max-w-2xl">
              <span className="overline">O que fazemos</span>
              <h2 className="font-display mt-4 text-3xl md:text-5xl font-medium tracking-tight leading-[1.08]">
                Escopo completo de{" "}
                <span className="text-white/60 italic font-light">infraestrutura</span>.
              </h2>
              <p className="mt-5 text-[15px] text-[#b3b0c2] max-w-xl">
                Tudo que sua operação precisa para funcionar com qualidade, redundância e
                documentação — em um único time.
              </p>
            </div>

            <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {SERVICES.map((s, i) => {
                const Icon = s.icon;
                return (
                  <article
                    key={s.title}
                    data-testid={`infra-service-${i}`}
                    className="relative rounded-2xl border border-white/10 bg-[#0A0810] p-6 md:p-7 transition-all hover:-translate-y-1 hover:border-white/25 hover:shadow-[0_8px_32px_rgba(76,46,155,0.2)] group"
                  >
                    <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl border border-white/10 bg-gradient-to-br from-[#1a103c] to-[#0A0810]">
                      <Icon size={18} strokeWidth={1.5} />
                    </span>
                    <h3 className="font-display mt-6 text-[18px] font-medium leading-snug">
                      {s.title}
                    </h3>
                    <p className="mt-3 text-[13.5px] text-[#a19eb0] leading-relaxed">{s.desc}</p>
                    <div className="mt-6 h-px bg-gradient-to-r from-white/15 to-transparent" />
                  </article>
                );
              })}
            </div>
          </div>
        </section>

        {/* PROCESS */}
        <section className="relative py-20 md:py-28 bg-[#07060c] border-y border-white/5">
          <div className="max-w-7xl mx-auto px-6 md:px-8">
            <div className="max-w-2xl">
              <span className="overline">Como executamos</span>
              <h2 className="font-display mt-4 text-3xl md:text-5xl font-medium tracking-tight leading-[1.08]">
                Um processo pensado para{" "}
                <span className="text-white/60 italic font-light">não ter surpresas</span>.
              </h2>
            </div>

            <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-4 gap-5">
              {PROCESS.map((p) => (
                <div
                  key={p.step}
                  className="rounded-2xl border border-white/10 bg-[#0A0810] p-7 relative"
                >
                  <div className="font-display text-5xl text-white/15">{p.step}</div>
                  <h3 className="font-display mt-5 text-xl font-medium">{p.title}</h3>
                  <p className="mt-3 text-[14px] text-[#a19eb0] leading-relaxed">{p.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* WHY US */}
        <section className="relative py-20 md:py-28">
          <div className="max-w-7xl mx-auto px-6 md:px-8 grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-6">
              <span className="overline">Por que escolher a Nexus</span>
              <h2 className="font-display mt-4 text-3xl md:text-5xl font-medium tracking-tight leading-[1.08]">
                Execução{" "}
                <span className="text-white/60 italic font-light">profissional</span> e entrega
                documentada.
              </h2>
              <ul className="mt-8 space-y-4 max-w-xl">
                {[
                  "Equipe própria e treinada — sem terceirização",
                  "Materiais e conectores homologados",
                  "Certificação técnica com laudos e diagramas",
                  "Respeito a cronograma e ao ambiente do cliente",
                  "Suporte pós-entrega e parceria de longo prazo",
                ].map((b) => (
                  <li key={b} className="flex items-start gap-3 text-[15px] text-white/85">
                    <CheckCircle2 size={18} className="text-emerald-300 mt-0.5" />
                    {b}
                  </li>
                ))}
              </ul>
            </div>
            <div className="lg:col-span-6">
              <div className="relative rounded-[28px] overflow-hidden border border-white/10">
                <img
                  src="https://static.prod-images.emergentagent.com/jobs/45876ead-02aa-42fd-bdbd-b2c7f606006c/images/ffee4e28c0ab598b7a4eb09798d58c27474667942f1f5b5dff0cc462a17910e3.png"
                  alt="Data center Grupo Nexus"
                  className="w-full h-[440px] object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="relative py-20 md:py-28">
          <div className="max-w-7xl mx-auto px-6 md:px-8">
            <div className="nx-rainbow-border relative overflow-hidden rounded-[28px] border border-white/10 bg-gradient-to-br from-[#12093a] via-[#0A0810] to-black p-10 md:p-16 text-center" data-active="true">
              <div className="nx-blob bg-[#4c2e9b] w-[500px] h-[500px] -top-40 -left-32" aria-hidden />
              <div className="nx-blob bg-[#00f0ff]/30 w-[360px] h-[360px] -bottom-24 -right-10" aria-hidden />
              <div className="relative max-w-3xl mx-auto">
                <span className="overline">Vamos construir juntos</span>
                <h2 className="font-display mt-5 text-3xl md:text-5xl font-medium tracking-tight leading-[1.05]">
                  Solicite um orçamento de{" "}
                  <span className="nx-rainbow-text">Infraestrutura Técnica</span>.
                </h2>
                <p className="mt-5 text-[15px] md:text-base text-[#b3b0c2]">
                  Nossa equipe avalia o escopo, dimensiona o projeto e apresenta uma proposta
                  clara com prazo e cronograma. Fale conosco pelo WhatsApp.
                </p>
                <div className="mt-9 flex flex-wrap justify-center gap-3">
                  <a
                    href={WPP_INFRA}
                    target="_blank"
                    rel="noreferrer noopener"
                    data-testid="infra-cta-whatsapp"
                    className="nx-btn-primary"
                  >
                    <MessageCircle size={16} /> WhatsApp {SITE.phoneLabel}
                  </a>
                  <Link to="/#contato" className="nx-btn-secondary">
                    Enviar mensagem
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
};

export default InfraestruturaPage;
